<template>
  <article class="lab-page" :lang="zh ? 'zh-Hant' : 'en'">
    <!-- 頁首：這是什麼。左邊一句他的話與說明，右邊一件真的在跑的作品（桌機捲到這裡自己開始，手機按了才跑） -->
    <header class="lp-hero">
      <div class="lp-hero-text">
        <!-- 2023 品牌圖示 Lab（L、A、B 上下鏡射疊成＝實驗室這個詞本身，也是鏡射、複製、育種）當頁面的印章；2026-10-04 -->
        <BrandIcon name="lab" tight class="lp-mark" />
        <p class="lp-eyebrow">CHE-YU WU · LAB · {{ stats.yearFrom }}–{{ stats.yearTo }}</p>
        <h1>{{ t.title }}<small>{{ t.titleSub }}</small></h1>
        <blockquote class="quote lp-motto">
          <p>{{ t.motto }}</p>
          <cite>{{ t.mottoCite }}</cite>
        </blockquote>
        <p class="lp-lede">{{ t.lede }}</p>
        <div class="lp-actions">
          <a class="primary" :href="LAB + '/'" rel="noopener">{{ t.enter }} ↗︎</a>
          <a :href="LAB + '/all/'" rel="noopener">{{ t.all }} ↗︎</a>
          <a :href="LAB + '/lineage/'" rel="noopener">{{ t.lineage }} ↗︎</a>
        </div>
      </div>
      <figure class="lp-specimen gene-host" :aria-label="t.specimenLabel">
        <div ref="frameEl" class="lp-frame" :class="{ 'is-running': running, 'is-live': frameLoaded }" :style="{ aspectRatio: hero.w + ' / ' + hero.h }">
          <!-- 手機先放 20px 的模糊小圖（約 200 bytes，低於 Chrome 認 LCP 的門檻，手機的 LCP 留給頁首文字），作品跑起來之後或不自動跑的人才換 480 海報；
               桌機 960 寬、優先載入（它是桌機的 LCP）。2026-10-04：手機版作品排到大標下面後，480 海報成了 LCP，Lighthouse 手機 93 → 45 -->
          <picture>
            <source media="(max-width: 960px)" :srcset="fullPoster ? `/static/lab-home/${hero.id}-poster-480.webp` : hero.lqip" />
            <img
              class="lp-poster" :class="{ 'is-lqip': !fullPoster }"
              :src="`/static/lab-home/${hero.id}-poster-960.webp`"
              :width="hero.w" :height="hero.h"
              :alt="t.posterAlt(name(hero))"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
          <iframe
            v-if="running"
            :key="hero.id"
            class="lp-live"
            :src="hero.run + '?kiosk=1'"
            :title="t.frameTitle(name(hero))"
            :width="hero.w" :height="hero.h"
            :style="{ width: hero.w + 'px', height: hero.h + 'px', transform: `scale(${scale})` }"
            sandbox="allow-scripts allow-same-origin allow-pointer-lock"
            allow="accelerometer; gyroscope"
            referrerpolicy="strict-origin-when-cross-origin"
            tabindex="-1"
            @load="onFrameLoad"
          ></iframe>
          <!-- 預設進頁就自己跑（哲宇 10/04：「不用『在這裡跑起來』按鈕，直接跑起來」）；只有減少動態、省流量或按了停止的人才看到這顆小播放鍵 -->
          <button v-if="!running && held" type="button" class="lp-play-mini" @click="start(true)">
            <BrandIcon name="run" />{{ t.playBtn }}
          </button>
        </div>
        <figcaption class="lp-specimen-bar">
          <p class="lp-status" aria-live="polite">
            <span class="lp-dot" :class="{ on: running && frameLoaded }" aria-hidden="true"></span>{{ running ? (frameLoaded ? t.statusRunning : t.statusLoading) : held ? (userStopped ? t.statusStopped : t.statusHeld) : t.statusPoster }}
          </p>
          <div class="lp-frame-actions">
            <button v-if="running" type="button" class="lp-btn-icon" @click="stop"><BrandIcon name="stop" />{{ t.stop }}</button>
            <button v-else type="button" class="lp-btn-icon" @click="start(true)"><BrandIcon name="run" />{{ t.playBtn }}</button>
            <button type="button" @click="next">{{ t.next }}</button>
            <a :href="hero.run" rel="noopener">{{ t.openRun }} ↗︎</a>
          </div>
        </figcaption>
        <p class="lp-specimen-title">
          <a :href="hero.url" rel="noopener"><b>{{ name(hero) }}</b><span v-if="other(hero)" :lang="zh ? 'en' : 'zh-Hant'">{{ other(hero) }}</span></a>
          <code>{{ hero.id }}</code>
        </p>
        <!-- 頁首作品的基因條（2026-10-05）：作品頁那枚「標本標籤」的樣子（320×20、手機 240×16），跟作品名一組、換一件就跟著換。
             來源是這件的 960 海報（桌機已經是第一屏的圖，手機等頁面 load＋閒置之後才載，不跟第一屏搶頻寬），標籤窗見 build-gene-strips。 -->
        <span class="lp-hero-gene"><GeneStrip v-if="geneReady && heroGene" :key="hero.id" :slug="hero.id" :src="heroGene" :meta="labGenes.sources[heroGene]" label /></span>
      </figure>
    </header>

    <!-- 數字：有多大、多久，一列六個。建置時讀 lab 的公開資料，讀不到就用快照並標日期。每年的件數長條搬到 03 每日創作的年份列 -->
    <section class="lp-numbers" :aria-label="t.numbersLabel">
      <dl class="lp-stats">
        <div><dt>{{ t.sTotal }}</dt><dd>{{ stats.total }}</dd></div>
        <div><dt>{{ t.sFeatured }}</dt><dd>{{ stats.featured }}</dd></div>
        <div><dt>{{ t.sPoems }}</dt><dd>{{ stats.poems }}</dd></div>
        <div><dt>{{ t.sFamilies(stats.pairs) }}</dt><dd>{{ stats.families }}</dd></div>
        <!-- 最深的一支是哪一家：數字旁邊寫名字（2026-10-05，原本只有「15 代」，下面族譜畫的卻是 8 代的 Bee） -->
        <div><dt>{{ t.sDeepest }}</dt><dd>{{ stats.deepest }}<small>{{ t.gens }}</small></dd><dd v-if="deepestName" class="lp-stat-of"><a :href="deepestUrl" rel="noopener">{{ deepestName }} ↗︎</a></dd></div>
        <div><dt>{{ t.sYears }}</dt><dd class="lp-years-dd">{{ stats.yearFrom }}–{{ stats.yearTo }}</dd></div>
      </dl>
      <p class="lp-source">{{ live ? t.sourceLive(statsDay) : t.sourceSnapshot(statsDay) }}</p>
    </section>

    <nav class="lp-nav" :aria-label="t.navLabel">
      <a v-for="s in t.sections" :key="s.id" :href="'#' + s.id">{{ s.label }}</a>
    </nav>

    <!-- 01 正在演化：實驗室裡新開、還在長的作品。名單、順序、公開與否都照 lab.cheyuwu.com/api/evolving.json（SSOT＝實驗室，哲宇 2026-10-05）。
         建置時讀（scripts/fetch-lab-stats.mjs），讀不到用快照，每天 04:40 排程重建。 -->
    <section id="lab-evolving" class="lp-section">
      <div class="lp-head">
        <p class="lp-eyebrow">01 · NOW EVOLVING</p>
        <h2>{{ t.evoTitle }}</h2>
        <p class="lp-note">{{ t.evoNote }}</p>
      </div>
      <LabEvolving v-if="evolving.works.length" :works="evolving.works" :zh="zh" :today="today" />
      <p v-else class="lp-note">{{ t.evoEmpty }}</p>
      <p class="lp-more">
        <a :href="evolving.page" rel="noopener">{{ t.evoAll(evolving.count) }} ↗︎</a>
      </p>
      <p class="lp-source">{{ evolvingLive ? t.evoSourceLive(evolving.fetchedOn) : t.evoSourceSnapshot(evolving.fetchedOn) }}</p>
    </section>

    <!-- 02 長成作品（2026-10-05）：官網作品頁的族譜各接回實驗室裡的哪一件每日創作，這件事只有官網講得出來。
         資料 src/data/lab-grown.json（scripts/build-lab-grown.mjs）。卡片跟 /work/、首頁精選同一套：圖、年份・類別、作品名、基因條、
         從哪件草圖長出來。基因條從卡片那張圖切（不多下載），條在文字區、不貼圖（DESIGN §二十三）。圖與條捲到附近才載。 -->
    <section id="lab-grown" ref="grownEl" class="lp-section">
      <div class="lp-head">
        <p class="lp-eyebrow">02 · GROWN INTO WORKS</p>
        <h2>{{ t.grownTitle }}</h2>
        <p class="lp-note">{{ t.grownNote(grown.length, grownSeeds) }}</p>
      </div>
      <ol class="lp-grown">
        <li v-for="g in grown" :key="g.slug" class="lp-grown-card gene-host">
          <span class="lp-grown-img"><img loading="lazy" decoding="async" :width="g.w" :height="g.h" :src="grownNear ? g.img : null" alt="" /></span>
          <p class="lp-grown-kicker">{{ g.year }} · {{ t.category(g.category) }}</p>
          <h3><RouterLink class="lp-stretch" :to="local('/work/' + g.slug)">{{ work(g) }}</RouterLink></h3>
          <span class="lp-grown-gene"><GeneStrip v-if="grownNear && geneOk(labGenes.sources[g.img])" :slug="g.slug" :src="g.img" :meta="labGenes.sources[g.img]" label /></span>
          <p class="lp-grown-from">
            <RouterLink v-if="g.sketch.page" class="lp-seed" :to="local(g.sketch.page)" tabindex="-1" aria-hidden="true"><img v-if="g.sketch.thumb" loading="lazy" decoding="async" width="240" height="240" :src="grownNear ? g.sketch.thumb : null" alt="" /></RouterLink>
            <span v-else class="lp-seed"><img v-if="g.sketch.thumb" loading="lazy" decoding="async" width="240" height="240" :src="grownNear ? g.sketch.thumb : null" alt="" /></span>
            <span class="lp-grown-from-text">{{ t.fromPre }}<RouterLink v-if="g.sketch.page" :to="local(g.sketch.page)">{{ sketchName(g.sketch) }}</RouterLink><b v-else>{{ sketchName(g.sketch) }}</b>{{ t.fromPost }}<small><time :datetime="g.sketch.date">{{ g.sketch.date }}</time> → {{ g.year }}<template v-if="!g.sketch.public"> · {{ t.fromPrivate }}</template></small></span>
          </p>
        </li>
        <!-- 最後一列的空位：接到全部作品與官網的族譜（grid-column: auto / -1，從這一格排到列尾） -->
        <li class="lp-grown-more">
          <RouterLink :to="local('/work')">{{ t.grownWorks }} →</RouterLink>
          <RouterLink :to="local('/lineage')">{{ t.officialLineage }} →</RouterLink>
        </li>
      </ol>
    </section>

    <!-- 03 牆：長什麼樣子。上面一列是每一年（件數、金色是精選、那一年的一條基因條，點了到實驗室那一年），下面 30 格 -->
    <section id="lab-wall" ref="wallEl" class="lp-section">
      <div class="lp-head">
        <p class="lp-eyebrow">03 · DAILY CODING</p>
        <h2>{{ t.wallTitle }}</h2>
        <p class="lp-note">{{ t.wallNote(stats.featured) }}</p>
      </div>
      <figure class="lp-bars">
        <ul :aria-label="t.barsLabel" :style="{ '--years': stats.years.length }">
          <li v-for="y in stats.years" :key="y.year" :style="{ '--h': (y.n / maxYear).toFixed(3), '--f': y.n ? (y.featured / y.n).toFixed(3) : 0 }">
            <a class="lp-year gene-host" :href="LAB + '/year/' + y.year + '/'" rel="noopener">
              <span class="lp-bar-n">{{ y.n }}</span>
              <span class="lp-bar" :title="t.barTitle(y)"><span class="lp-bar-f"></span></span>
              <span class="lp-bar-y">{{ y.year }}</span>
              <span class="lp-sr">{{ t.barTitle(y) }}</span>
              <span class="lp-year-gene"><GeneStrip v-if="wallNear && yearGene[y.year]" :slug="y.year" :src="yearGene[y.year]" :meta="labGenes.sources[yearGene[y.year]]" label /></span>
            </a>
          </li>
        </ul>
        <figcaption>{{ t.barsCaption }}</figcaption>
      </figure>
      <ul class="lp-wall">
        <li v-for="(w, i) in snap.wall" :key="w.id" :class="{ big: w.big, 'm-hide': mobileHidden.has(w.id) }">
          <a :href="w.url" rel="noopener">
            <!-- 牆的圖等捲到牆前 700px 才給 src（說明在 onMounted）。loading 寫在 src 前面：Vue 依模板順序設屬性，先設 src 會在設 lazy 之前就開始下載（2026-10-04 Lighthouse 抓到 52 張圖一掛載就全載） -->
            <img
              loading="lazy" decoding="async" width="480" height="480"
              :sizes="w.big ? '(max-width: 700px) 50vw, 26vw' : '(max-width: 700px) 25vw, 13vw'"
              :srcset="wallNear ? `/static/lab-home/${w.id}-240.webp 240w, /static/lab-home/${w.id}-480.webp 480w` : null"
              :src="wallNear ? `/static/lab-home/${w.id}-240.webp` : null"
              :alt="name(w)"
            />
            <span v-if="firstOfYear.has(i)" class="lp-year-chip" aria-hidden="true">{{ w.date.slice(0, 4) }}</span>
            <span class="lp-tile-meta"><b>{{ name(w) }}</b><time :datetime="w.date">{{ w.date }}</time></span>
          </a>
        </li>
      </ul>
      <p class="lp-more">
        <a :href="LAB + '/'" rel="noopener">{{ t.allFeatured(stats.featured) }} ↗︎</a>
        <a :href="LAB + '/all/'" rel="noopener">{{ t.all }} ↗︎</a>
      </p>
    </section>

    <!-- 04 族譜：怎麼長出來的 -->
    <section id="lab-lineage" ref="lineEl" class="lp-section lp-growth">
      <div class="lp-head">
        <p class="lp-eyebrow">04 · LINEAGE</p>
        <h2>{{ t.growthTitle }}</h2>
      </div>
      <div class="lp-growth-grid">
        <div class="lp-growth-text">
          <p class="lp-body">{{ familyStory }}</p>
          <template v-if="deepestNote">
            <p class="lp-body lp-deepest">{{ deepestNote }}</p>
            <p class="lp-links lp-deepest-link"><a :href="deepestUrl" rel="noopener">{{ t.thisFamily(deepestName) }} ↗︎</a></p>
          </template>
          <p class="lp-legend">{{ t.legend }}</p>
          <p class="lp-links">
            <a :href="family.url" rel="noopener">{{ t.thisFamily(familyName) }} ↗︎</a>
            <a :href="LAB + '/lineage/'" rel="noopener">{{ t.allFamilies(stats.families, stats.pairs) }} ↗︎</a>
            <RouterLink :to="local('/lineage')">{{ t.officialLineage }} →</RouterLink>
          </p>
          <div class="lp-recent">
            <h3>{{ t.recentTitle }}</h3>
            <ol>
              <li v-for="r in recent" :key="r.id" class="gene-host">
                <time :datetime="r.date">{{ r.date }}</time>
                <a :href="r.url" rel="noopener">{{ name(r) }}</a>
                <span class="lp-recent-gene"><GeneStrip v-if="lineNear && r.img && geneOk(r.gene)" :slug="r.id" :src="r.img" :meta="r.gene" label /></span>
              </li>
            </ol>
            <p class="lp-legend">{{ t.recentNote }}</p>
          </div>
        </div>
        <figure class="lp-tree-fig">
          <!-- 手機不再轉成直的（30px 的節點疊在一起、說明卻寫「每一欄是一代」）：一樣代＝欄，整張圖可以左右滑 -->
          <div class="lp-tree-scroll">
            <div class="lp-tree" :style="{ '--G': family.gens, '--R': family.rows }">
              <svg class="lp-edges" :viewBox="`0 0 ${family.gens} ${family.rows}`" preserveAspectRatio="none" aria-hidden="true">
                <path v-for="e in edges" :key="'h' + e.id" :d="e.h" :class="['lp-edge', e.kind, { gap: e.gap }]" vector-effect="non-scaling-stroke" />
              </svg>
              <span v-for="e in edges.filter((x) => x.gap)" :key="'gl' + e.id" class="lp-gap-label" :style="{ '--g': e.gx, '--r': e.gy }">{{ e.gap }}</span>
              <a
                v-for="n in family.nodes" :key="n.id"
                class="lp-node" :class="{ root: !n.parent, leaf: isLeaf(n.id) }"
                :href="n.url" rel="noopener"
                :style="{ '--g': n.gen - 1, '--r': n.row }"
                :title="`${name(n)} · ${n.date}`"
                :aria-label="t.nodeLabel(name(n), n.date, n.gen)"
              >
                <img loading="lazy" decoding="async" width="240" height="240" :src="`/static/lab-home/${n.id}-240.webp`" alt="" />
                <span class="lp-node-date">{{ n.date.slice(5).replace('-', '.') }}</span>
              </a>
            </div>
            <div class="lp-gens" :style="{ '--G': family.gens }">
              <span v-for="g in family.gens" :key="g" class="lp-gen" :style="{ '--g': g - 1 }">{{ t.gen(g) }}</span>
            </div>
          </div>
          <figcaption class="lp-tree-hint">{{ t.treeHint(family.gens) }}</figcaption>
        </figure>
      </div>
    </section>

    <!-- 05 詩：作品會說話。跟 lab 首頁同一個池，每次打開換一首 -->
    <section id="lab-poem" class="lp-section lp-poem">
      <div class="lp-head">
        <p class="lp-eyebrow">05 · A POEM</p>
        <h2>{{ t.poemTitle }}</h2>
        <p class="lp-note">{{ t.poemNote(stats.poems, snap.poems.length) }}</p>
      </div>
      <figure class="lp-poem-fig" :class="{ swapping }">
        <blockquote lang="zh-Hant" class="lp-poem-lines">
          <p><template v-for="(line, i) in poem.lines" :key="i"><span v-if="line" class="lp-line">{{ line }}</span><span v-else class="lp-line-gap" aria-hidden="true"></span></template></p>
        </blockquote>
        <figcaption class="lp-poem-cap">
          <a :href="poem.url" rel="noopener" class="lp-poem-thumb"><img loading="lazy" decoding="async" width="240" height="240" :src="`/static/lab-home/${poem.id}-240.webp`" alt="" /></a>
          <span class="lp-poem-meta">
            <a :href="poem.url" rel="noopener"><b>{{ name(poem) }}</b></a>
            <small><span v-if="other(poem)" :lang="zh ? 'en' : 'zh-Hant'">{{ other(poem) }} · </span><time :datetime="poem.date">{{ poem.date }}</time></small>
            <span class="lp-poem-actions">
              <button type="button" @click="nextPoem">{{ t.nextPoem }}</button>
              <a :href="poem.url" rel="noopener">{{ t.seePiece }} ↗︎</a>
            </span>
          </span>
        </figcaption>
      </figure>
    </section>

    <!-- 06 怎麼玩：每一項一句白話，加一個點得進去的連結。兩欄、直角、細線分隔，只開給哲宇的兩項併成最後一列 -->
    <section id="lab-play" class="lp-section">
      <div class="lp-head">
        <p class="lp-eyebrow">06 · PLAY</p>
        <h2>{{ t.playTitle }}</h2>
      </div>
      <ol class="lp-play-list">
        <li v-for="(p, i) in playOpen" :key="p.key">
          <span class="lp-play-no">{{ String(i + 1).padStart(2, '0') }}</span>
          <h3>{{ p.title }}</h3>
          <p>{{ p.body }}</p>
          <p class="lp-links">
            <a v-for="l in p.links" :key="l.href" :href="l.href" rel="noopener">{{ l.label }} ↗︎</a>
          </p>
        </li>
        <li class="lp-play-owner">
          <p class="lp-play-owner-label">{{ t.ownerOnly }}</p>
          <div v-for="(p, i) in playOwner" :key="p.key" class="lp-play-owner-item">
            <span class="lp-play-no">{{ String(playOpen.length + i + 1).padStart(2, '0') }}</span>
            <h3>{{ p.title }}</h3>
            <p>{{ p.body }}</p>
            <p class="lp-links">
              <a v-for="l in p.links" :key="l.href" :href="l.href" rel="noopener">{{ l.label }} ↗︎</a>
            </p>
          </div>
        </li>
      </ol>
    </section>

    <!-- 07 為什麼：他的原話（標日期），正文第三人稱 -->
    <section id="lab-why" class="lp-section lp-why">
      <div class="lp-head">
        <p class="lp-eyebrow">07 · WHY</p>
        <h2>{{ t.whyTitle }}</h2>
      </div>
      <div class="lp-why-body">
        <blockquote class="quote">
          <p>{{ t.why1 }}</p>
          <cite>{{ t.whyCite }}</cite>
        </blockquote>
        <p class="lp-body">{{ t.whyText1 }}</p>
        <blockquote class="quote">
          <p>{{ t.why2 }}</p>
          <cite>{{ t.whyCite }}</cite>
        </blockquote>
        <p class="lp-body">{{ t.whyText2 }}</p>
      </div>
    </section>

    <footer class="lp-outro">
      <a class="lp-outro-main" :href="LAB + '/'" rel="noopener"><small>{{ t.enterSmall }}</small>lab.cheyuwu.com ↗︎</a>
      <p class="lp-links">
        <RouterLink :to="local('/strata')">{{ t.strata(strataFrom, strataTo) }} →</RouterLink>
        <RouterLink :to="local('/lineage')">{{ t.officialLineage }} →</RouterLink>
        <a href="https://openaudiovisual.com/" rel="noopener">open-audiovisual ↗︎</a>
        <a :href="LAB + '/api/index.json'" rel="noopener">{{ t.api }} ↗︎</a>
      </p>
    </footer>
  </article>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import snap from '@/data/lab-home.json'
import strataIndex from '@/data/lab/index.json'
import { sitePath } from '@/runtime/site-path.mjs'
import { isBot, prefersReducedMotion } from '@/runtime/view-transition'
import BrandIcon from '@/components/BrandIcon.vue'
import LabEvolving from '@/components/lab/LabEvolving.vue'
import GeneStrip from '@/components/GeneStrip.vue'
import labGenes from '@/data/gene-strips-lab.json'
import grownData from '@/data/lab-grown.json'

// 資料：牆、族譜、詩、頁首作品與縮圖是 commit 的快照（scripts/fetch-lab-snapshot.mjs）；
// 數字、年份長條、「最近長出來的」與「正在演化」由建置時的 scripts/fetch-lab-stats.mjs 讀 lab 現況（src/data/lab-live.json、lab-live-evolving.json，不進 git），
// 兩份各自讀不到就各自用快照，並寫是哪一天的快照。執行期不 fetch lab：只有頁首的 iframe 會去開 lab 的執行頁。
const liveFiles = import.meta.glob('../../data/lab-live.json', { eager: true, import: 'default' })
const live = Object.values(liveFiles)[0] || null
const stats = live?.stats || snap.stats
const recent = live?.recent || snap.recent
const statsDay = live?.fetchedOn || snap.fetchedOn
const liveEvoFiles = import.meta.glob('../../data/lab-live-evolving.json', { eager: true, import: 'default' })
const liveEvolving = Object.values(liveEvoFiles)[0] || null
const evolvingLive = Array.isArray(liveEvolving?.works)
const evolving = evolvingLive ? liveEvolving : snap.evolving
// 「N 天前」的今天：預渲染時是建置那天，讀者打開時是當天（台北日期）
const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date())
const LAB = 'https://lab.cheyuwu.com'

const route = useRoute()
const zh = computed(() => route.path.startsWith('/zh/') || route.path === '/zh')
const local = (p) => sitePath(p, zh.value)
const name = (x) => (zh.value ? x.titleZh || x.title : x.title)
const other = (x) => (x.titleZh && x.titleZh !== x.title ? (zh.value ? x.title : x.titleZh) : '')
const strataFrom = Math.min(...strataIndex.map((e) => Number(e.period.start.slice(0, 4))))
const strataTo = Math.max(...strataIndex.map((e) => Number(e.period.end.slice(0, 4))))
const maxYear = Math.max(...stats.years.map((y) => y.n))

// 同一次建置，預渲染與瀏覽器挑到同一件／同一首（不會一掛載就換圖，LCP 不跳）；之後「換一件」「換一首」才隨機
const hash = (s) => { let h = 2166136261; for (const c of String(s)) { h ^= c.codePointAt(0); h = Math.imul(h, 16777619) >>> 0 } return h }
// 進頁第一件從「手機也順」的幾件挑（snap.hero[].mobile，快照腳本量過），手機桌機同一件，預渲染的海報掛載後不會被換掉
const mobileHeroes = snap.hero.map((h, i) => (h.mobile ? i : -1)).filter((i) => i >= 0)
const firstPool = mobileHeroes.length ? mobileHeroes : snap.hero.map((_, i) => i)
const heroIdx = ref(firstPool[hash(statsDay) % firstPool.length])
const hero = computed(() => snap.hero[heroIdx.value])
const poemIdx = ref(hash(statsDay + 'poem') % snap.poems.length)
const poem = computed(() => snap.poems[poemIdx.value])

// 牆：每年第一格標年份；手機藏 8 個小格（4 欄 × 10 列剛好填滿）
const wallEl = ref(null)
const wallNear = ref(false)
let wallIo = null
// 長成作品、族譜（最近長出來的基因條）：同一套「捲到附近才給圖」。基因條被 scaleY(500) 拉長，loading="lazy" 對它無效（HomeGene.vue 的說明）
const grownEl = ref(null), grownNear = ref(false)
const lineEl = ref(null), lineNear = ref(false)
const nearIos = []
// 頁首的基因條：等頁面 load＋閒置（跟頁首 iframe 同一個時間點），手機才不會在第一屏多載一張 960 海報
const geneReady = ref(false)
const firstOfYear = new Set(snap.wall.map((w, i) => (i === 0 || snap.wall[i - 1].date.slice(0, 4) !== w.date.slice(0, 4) ? i : -1)).filter((i) => i >= 0))
const mobileHidden = new Set(snap.wall.filter((w) => !w.big).filter((_, i) => i % 3 === 2).slice(0, 8).map((w) => w.id))

// 族譜：代＝欄（手機也一樣，整張圖左右滑，2026-10-05 以前手機轉成列），葉子各佔一列，兩代之間隔超過半年就標「N 年後」
const family = snap.family
const nodeById = new Map(family.nodes.map((n) => [n.id, n]))
const parents = new Set(family.nodes.map((n) => n.parent).filter(Boolean))
const isLeaf = (id) => !parents.has(id)
const monthsBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / (30.44 * 864e5))
const edges = computed(() => family.nodes.filter((n) => n.parent && nodeById.has(n.parent)).map((n) => {
  const p = nodeById.get(n.parent)
  const x1 = p.gen - 0.5, y1 = p.row + 0.5, x2 = n.gen - 0.5, y2 = n.row + 0.5, mx = (x1 + x2) / 2
  const months = monthsBetween(p.date, n.date)
  return {
    id: n.id, kind: n.kind,
    h: `M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`,
    gap: months >= 6 ? (months >= 12 ? t.value.yearsLater(Math.round(months / 12)) : t.value.monthsLater(months)) : '',
    gx: mx - 0.5, gy: (y1 + y2) / 2 - 0.5,
  }
}))
const familyName = computed(() => (zh.value ? family.nameZh || family.name : family.name))
const familyStory = computed(() => (family.id === '210112-bee' ? t.value.beeStory : t.value.familyGeneric(familyName.value, family.size, family.gens)))

// ── 長成作品（src/data/lab-grown.json）──
const grown = grownData.items
const grownSeeds = new Set(grown.map((g) => g.sketch.lab || g.sketch.op)).size
const work = (g) => (zh.value ? g.titleZh || g.title : g.title)
const sketchName = (k) => (zh.value ? k.titleZh || k.title : k.title)

// 最深的一支族譜是哪一家（數字列與族譜段落都寫名字，跟畫出來的 Bee 不是同一家時多一段話）。官網作品從它長出來的也點名
const deepestName = stats.deepestName || grown.find((g) => g.sketch.lab === stats.deepestFamily)?.sketch.title || null
const deepestUrl = `${LAB}/family/${stats.deepestFamily}/`
const deepestNote = computed(() => {
  if (!deepestName || !stats.deepestFamily || stats.deepestFamily === family.id) return ''
  const works = grown.filter((g) => g.sketch.lab === stats.deepestFamily).sort((a, b) => a.year - b.year).map(work)
  return t.value.deepestNote(deepestName, stats.deepestSize, stats.deepest, works, familyName.value, family.size, family.gens)
})

// ── 基因條（GeneStrip.vue）：實驗室頁的圖在 build-gene-strips 用標籤窗 tl 算過，寫在 gene-strips-lab.json（只有這頁讀，傳 meta），
//    最近長出來的在建置時算（scripts/lib/lab-recent.mjs）──
// 標籤窗那一列近黑、灰（單色作品）或暗段太多就不放：條碼讀起來會像沒載完（哲宇 10/04「看起來不像基因，比較像是壞掉」）
const geneOk = (m) => !!(m && m.tl && m.lc && m.lc.lum >= 30 && m.lc.chroma >= 8 && m.lc.dark <= 0.35)
const heroGene = computed(() => { const src = `/static/lab-home/${hero.value.id}-poster-960.webp`; return geneOk(labGenes.sources[src]) ? src : null })
// 每一年一條：牆上那一年的縮圖裡，標籤窗最乾淨、顏色最多的那張
const yearGene = Object.fromEntries(stats.years.map((y) => {
  const pick = snap.wall.filter((w) => w.date.startsWith(y.year)).map((w) => `/static/lab-home/${w.id}-480.webp`)
    .filter((u) => geneOk(labGenes.sources[u])).sort((a, b) => { const A = labGenes.sources[a].lc, B = labGenes.sources[b].lc; return (B.chroma * (1 - B.dark)) - (A.chroma * (1 - A.dark)) })[0]
  return [y.year, pick || null]
}))

// ── 頁首作品：進頁就跑（桌機手機都是），但等頁首文字與圖畫完（load＋閒置）才載 iframe，不跟第一屏搶頻寬 ──
// 捲出畫面超過 1.2 秒或分頁切到背景就卸載（省電），回來再跑。減少動態、省流量的人不自動跑，給一顆小播放鍵（無障礙例外）。
// 停止＝拿掉 iframe 回到海報（跨網域碰不到作品的迴圈，沒有真的暫停）；按過停止就不再自動開始，直到再按播放。
const frameEl = ref(null)
const running = ref(false)
const frameLoaded = ref(false)
const scale = ref(0.5)
const userStopped = ref(false)
const autoOk = ref(true) // 掛載後依減少動態／省流量改寫；預渲染時是 true（沒有小播放鍵）
const held = computed(() => userStopped.value || !autoOk.value)
const everLive = ref(false)
const fullPoster = computed(() => held.value || everLive.value)
let io = null, ro = null, settleTimer = 0, leaveTimer = 0, delayTimer = 0, armed = false, inView = false
const fitScale = () => { if (frameEl.value) scale.value = frameEl.value.clientWidth / hero.value.w }
function start(byUser = false) {
  if (byUser) userStopped.value = false
  if (running.value) return
  fitScale()
  frameLoaded.value = false
  running.value = true
}
function unload() { running.value = false; frameLoaded.value = false; clearTimeout(settleTimer) }
function stop() { userStopped.value = true; unload() }
const narrow = () => typeof matchMedia === 'function' && matchMedia('(max-width: 900px), (pointer: coarse)').matches
function next() {
  // 手機只在「手機也順」的幾件裡換；桌機全部都可以
  const pool = narrow() && mobileHeroes.length > 1 ? mobileHeroes : snap.hero.map((_, i) => i)
  let i = heroIdx.value
  if (pool.length > 1) while (i === heroIdx.value) i = pool[Math.floor(Math.random() * pool.length)]
  heroIdx.value = i
  frameLoaded.value = false
  fitScale()
}
// iframe 的 load 先到，作品第一格稍晚才畫；等一下再把海報淡掉，避免中間閃一格黑
function onFrameLoad() { clearTimeout(settleTimer); settleTimer = setTimeout(() => { frameLoaded.value = true; everLive.value = true }, 700) }
const wantsAuto = () => {
  if (isBot() || prefersReducedMotion()) return false
  const c = typeof navigator !== 'undefined' && navigator.connection
  return !(c && c.saveData)
}
const WAKE = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'], WAKE_OPT = { passive: true }
let onWake = () => {}
const maybeRun = () => { if (armed && inView && !userStopped.value && autoOk.value && !document.hidden) start() }
function onVisibility() { if (document.hidden) unload(); else maybeRun() }

// ── 詩：換一首（隨機、不跟這一首或上一次看的同一首） ──
const swapping = ref(false)
const POEM_KEY = 'cyw-lab-poem-last'
function pickPoem() {
  let last = null
  try { last = localStorage.getItem(POEM_KEY) } catch { /* 無痕或封鎖：只是可能重複 */ }
  let i = poemIdx.value, n = 0
  do { i = Math.floor(Math.random() * snap.poems.length); n++ } while ((i === poemIdx.value || snap.poems[i].id === last) && n < 24)
  poemIdx.value = i
  try { localStorage.setItem(POEM_KEY, snap.poems[i].id) } catch { /* 同上 */ }
}
function nextPoem() {
  if (prefersReducedMotion()) return pickPoem()
  swapping.value = true
  setTimeout(() => { pickPoem(); swapping.value = false }, 180)
}

onMounted(() => {
  if (!isBot()) pickPoem() // 每次打開換一首（預渲染的 HTML 裡是建置當天那一首）
  if (typeof ResizeObserver === 'function' && frameEl.value) { ro = new ResizeObserver(fitScale); ro.observe(frameEl.value) }
  // 牆的圖：捲到牆前 700px 才給 src；預渲染（無頭瀏覽器＝isBot 且 navigator.webdriver，同 main.js 的判斷）完全不給，靜態 HTML 裡沒有牆的 src。
  // 靜態 HTML 裡的 lazy 圖在手機上會被瀏覽器當成「快到了」先下載，跟頁首海報（手機的 LCP）搶頻寬（2026-10-04 量到 22 張）。
  // 文字與連結照樣在 HTML 裡；Googlebot 這類會跑 JS 的爬蟲不是 webdriver，照樣拿得到圖。
  const prerendering = isBot() && typeof navigator !== 'undefined' && navigator.webdriver === true
  if (prerendering) { /* 不給牆的 src */ } else if (typeof IntersectionObserver === 'function' && wallEl.value) {
    wallIo = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { wallNear.value = true; wallIo.disconnect(); wallIo = null } }, { rootMargin: '700px 0px' })
    wallIo.observe(wallEl.value)
  } else wallNear.value = true
  // 長成作品的圖與條、最近長出來的條：同上（預渲染不給）
  for (const [el, flag] of [[grownEl, grownNear], [lineEl, lineNear]]) {
    if (prerendering) continue
    if (typeof IntersectionObserver !== 'function' || !el.value) { flag.value = true; continue }
    const o = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { flag.value = true; o.disconnect() } }, { rootMargin: '700px 0px' })
    o.observe(el.value); nearIos.push(o)
  }
  if (isBot()) return
  autoOk.value = wantsAuto()
  if (typeof IntersectionObserver === 'function' && frameEl.value) {
    io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1]
      inView = e.isIntersecting
      clearTimeout(leaveTimer)
      if (inView) maybeRun()
      else leaveTimer = setTimeout(() => { if (!inView) unload() }, 1200)
    }, { threshold: 0.2 })
    io.observe(frameEl.value)
  } else inView = true
  document.addEventListener('visibilitychange', onVisibility)
  // 等頁首文字與圖片畫完：load 之後再等一次閒置。手機與慢機再多等 2.5 秒，或等第一次觸控／捲動（先到先開）：
  // lab.cheyuwu.com 跟 cheyuwu.com 同一個網站（eTLD+1），iframe 跟這一頁共用主執行緒（Android 的 Chrome 也不分行程），
  // p5.js 600 KB 剖析在慢手機上是一秒多的長任務（2026-10-04 正式站 Lighthouse 手機 TBT 1,020–1,100 ms，全部來自 p5.js）。
  const arm = onWake = () => { if (armed) return; armed = true; clearTimeout(delayTimer); for (const ev of WAKE) removeEventListener(ev, onWake, WAKE_OPT); maybeRun() }
  const slow = narrow() || (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4
  const later = () => { geneReady.value = true; if (!slow) return arm(); delayTimer = setTimeout(arm, 2500); for (const ev of WAKE) addEventListener(ev, onWake, WAKE_OPT) }
  const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(later, { timeout: 2500 }) : setTimeout(later, 1200))
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
})
onBeforeUnmount(() => { io?.disconnect(); ro?.disconnect(); wallIo?.disconnect(); for (const o of nearIos) o.disconnect(); clearTimeout(settleTimer); clearTimeout(leaveTimer); clearTimeout(delayTimer); for (const ev of WAKE) removeEventListener(ev, onWake, WAKE_OPT); document.removeEventListener('visibilitychange', onVisibility) })

const fmtDay = (d, isZh) => {
  const [y, m, dd] = d.split('-').map(Number)
  return isZh ? `${y} 年 ${m} 月 ${dd} 日` : new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
}
const listZh = (a) => (a.length < 2 ? a.join('') : a.slice(0, -1).join('、') + '與' + a[a.length - 1])
const listEn = (a) => (a.length < 2 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1])
const messages = {
  zh: {
    title: '實驗室',
    titleSub: 'Lab',
    motto: '我想要每個作品都是一個數位生命體，這樣更好玩一點。',
    mottoCite: '吳哲宇，2026 年 10 月',
    lede: `${stats.yearFrom} 年起，吳哲宇把每日創作寫在 OpenProcessing 上。2026 年 10 月 3 日，這 ${stats.movedIn ?? stats.total} 件搬進他自己架的實驗室，每件留著版本、fork 的來處，有的附著當年寫的詩。之後新開的作品，還會記下他做的每一個決定。`,
    enter: '進入實驗室',
    all: '全部創作',
    lineage: '族譜',
    specimenLabel: '一件正在實驗室裡跑的作品',
    posterAlt: (n) => `${n}，在實驗室執行約十秒時的畫面`,
    frameTitle: (n) => `${n}（從 lab.cheyuwu.com 執行）`,
    playBtn: '播放',
    stop: '停止',
    next: '換一件',
    openRun: '開新視窗',
    statusRunning: '正在執行，作品從 lab.cheyuwu.com 直接跑',
    statusLoading: '載入中',
    statusPoster: '執行十秒時的畫面，作品馬上會在這裡跑起來',
    statusHeld: '你的裝置設了減少動態或省流量，按播放才會跑',
    statusStopped: '已停止，按播放再跑一次',
    numbersLabel: '實驗室的數字',
    sTotal: '件作品',
    sFeatured: '件精選，他挑的',
    sPoems: '首詩',
    sFamilies: (pairs) => `個家族，另有 ${pairs} 對雙生`,
    sDeepest: '最深的一支族譜',
    gens: '代',
    sYears: '年份',
    barsLabel: '每年的件數',
    barTitle: (y) => `${y.year} 年：${y.n} 件，精選 ${y.featured} 件`,
    barsCaption: '每年的件數，金色是精選',
    sourceLive: (d) => `數字讀自 lab.cheyuwu.com 的公開資料（/api/index.json、/api/lineage.json），${fmtDay(d, true)}建置時讀取。`,
    sourceSnapshot: (d) => `數字來自 ${fmtDay(d, true)}的快照：這次建置連不到 lab.cheyuwu.com，下次建置會再讀一次。`,
    navLabel: '實驗室頁的章節',
    sections: [
      { id: 'lab-evolving', label: '正在演化' },
      { id: 'lab-grown', label: '長成作品' },
      { id: 'lab-wall', label: '每日創作' },
      { id: 'lab-lineage', label: '一件長出一件' },
      { id: 'lab-poem', label: '一首詩' },
      { id: 'lab-play', label: '怎麼玩' },
      { id: 'lab-why', label: '為什麼' },
    ],
    wallTitle: '每日創作',
    wallNote: (f) => `從 ${f} 件精選裡，依每年的件數比例挑 30 件，由舊到新排，放大的六件附著詩。點開就進到實驗室，作品在頁面裡跑。`,
    allFeatured: (f) => `看全部 ${f} 件精選`,
    evoTitle: '正在演化',
    evoNote: '實驗室裡新開、還在長的作品，最近改過的排在前面。名單與順序跟實驗室的〈正在演化〉同一份，未公開的只放名字與縮圖。',
    evoEmpty: '現在沒有正在演化的作品。',
    evoAll: (n) => `在實驗室看全部 ${n} 件`,
    evoSourceLive: (d) => `名單讀自 lab.cheyuwu.com 的公開資料（/api/evolving.json），${fmtDay(d, true)}建置時讀取。`,
    evoSourceSnapshot: (d) => `名單來自 ${fmtDay(d, true)}的快照：這次建置連不到 lab.cheyuwu.com，下次建置會再讀一次。`,
    grownTitle: '長成作品',
    grownNote: (n, k) => `官網上有 ${n} 件作品的族譜接得回實驗室：${k} 件每日創作，後來長成展出或發行的作品。依草圖的日期由舊到新排，基因條從作品的圖切出來，點草圖的名字回到那一天的程式。`,
    category: (c) => ({ installation: '裝置', generative: '生成藝術', nft: '生成藝術', opensource: '開源計畫', research: '研究' })[c] || c,
    fromPre: '從〈',
    fromPost: '〉長出來',
    fromPrivate: '草圖未公開',
    grownWorks: '全部作品',
    growthTitle: '一件長出一件',
    beeStory: '2021 年 1 月 12 日，吳哲宇寫了一件叫《Bee》的草圖，當天又 fork 出第二版。兩年後的 2023 年 5 月 20 日，這段程式長成〈三個階段的重構現象〉，接下來的十四天裡又長出十六件，一路傳到第八代的〈羽化〉。',
    familyGeneric: (n, size, gens) => `《${n}》家族：${size} 件作品，傳了 ${gens} 代。`,
    legend: '每一欄是一代，連線是 fork，點任何一件進到實驗室。',
    thisFamily: (n) => `看《${n}》這一家`,
    allFamilies: (f, p) => `全部 ${f} 個家族、${p} 對雙生`,
    officialLineage: '官網的創作族譜',
    deepestNote: (name, size, gens, works, fam, fsize, fgens) => `實驗室裡傳得最深的是《${name}》這一家，${size ? `${size} 件傳了 ` : '傳了 '}${gens} 代${works.length ? `，官網的${listZh(works.map((w) => `《${w}》`))}都從它長出來` : ''}。這張圖畫的是《${fam}》這一家，${fsize} 件、${fgens} 代。`,
    treeHint: (g) => `左右滑動，看完 ${g} 代`,
    recentTitle: '最近長出來的',
    recentNote: '依日期新到舊，教學示範不列，跟實驗室〈全部創作〉同一份。',
    nodeLabel: (n, d, g) => `${n}，${d}，第 ${g} 代`,
    gen: (g) => `第 ${g} 代`,
    yearsLater: (y) => `${y} 年後`,
    monthsLater: (m) => `${m} 個月後`,
    poemTitle: '一首詩',
    poemNote: (all, pure) => `實驗室有 ${all} 件作品附著詩，其中 ${pure} 首是純詩。這裡跟實驗室首頁用同一個池，每次打開換一首。`,
    nextPoem: '換一首',
    seePiece: '看這件',
    playTitle: '怎麼玩',
    ownerOnly: '目前只開給哲宇',
    play: [
      { key: 'run', title: '在頁面裡跑', body: '每件作品打開就在頁面裡跑，有些會跟著滑鼠動，開新視窗可以全螢幕。', links: [{ label: '試〈捲曲藤蔓〉', href: `${LAB}/s/200925-curvy-vine/` }, { label: '開新視窗', href: `${LAB}/run/200925-curvy-vine/` }] },
      { key: 'midi', title: 'MIDI', body: '用到 MIDI 的作品，右下角有一個 🎹，打開是螢幕上的控制器（MiniLab 3、LPD8、Launchpad、nanoKONTROL2），滑鼠就能彈。接上實體控制器，作品就跟著你的手。', links: [{ label: '試〈原始光裂片〉', href: `${LAB}/s/230616-prebiotic-flake/` }, { label: '控制器的原始碼', href: 'https://openaudiovisual.com/zh/#work-09' }] },
      { key: 'phone', owner: true, title: '手機控制器', body: '在工作室裡，每件作品有自己的房間，手機掃 QR 就變成它的控制台，傾斜、琴鍵、滑桿都推得動作品。這一層目前只開給哲宇，控制器本身開源在 open-audiovisual。', links: [{ label: '控制器的原始碼', href: 'https://openaudiovisual.com/zh/#work-08' }] },
      { key: 'talk', owner: true, title: '跟作品講話', body: '取了名字、寫了詩，作品就可以開口。目前只有哲宇登入後能講，作品頁上的 💬 就是那扇門。', links: [{ label: '〈繁盛〉的作品頁', href: `${LAB}/s/221230-flourishing/` }] },
      { key: 'fork', title: 'fork', body: '每件作品都可以從另一件長出來，只帶程式、只帶記憶，或兩者都帶。族譜把每一次 fork 畫出來，從任何一件往上找得到源頭。', links: [{ label: '實驗室的族譜', href: `${LAB}/lineage/` }] },
      { key: 'api', title: '給程式與 AI 讀', body: '每件公開作品的資料都在 /api/ 底下，程式與 AI agent 不用開瀏覽器也讀得到。', links: [{ label: '/api/index.json', href: `${LAB}/api/index.json` }] },
    ],
    whyTitle: '為什麼自己蓋一間',
    why1: '我未來的做作品原則，一個是毫無阻力的做實驗……另一個就是當我進到一個場域、媒材、空間或媒介的時候，我要盡可能每一次初期都降低我未來的阻力，把一切變成經驗值。',
    whyCite: '吳哲宇，2026 年 10 月 3 日',
    whyText1: 'OpenProcessing 留下了版本與 fork，卻沒有地方放詩，也連不回他後來展出的作品。實驗室把這兩件事補上：作品頁有詩、有圖鑑，也連回官網裡從它長大的那件作品。',
    why2: '變快之後，反而長不出來的就是像我手繪。',
    whyText2: '所以新作品的第一顆種子，盡量來自他的手：iPad 上的一張速寫，或坐在鋼琴前彈的一段。',
    enterSmall: '進入實驗室',
    strata: (a, b) => `地層：${a}–${b} 每一年留下來的`,
    api: '公開資料 API',
  },
  en: {
    title: 'Lab',
    titleSub: '實驗室',
    motto: 'I want every work to be a digital life form. It is more fun that way.',
    mottoCite: 'Che-Yu Wu, October 2026 (translated from Chinese)',
    lede: `From ${stats.yearFrom}, Che-Yu Wu wrote his daily sketches on OpenProcessing. On 3 October 2026 all ${stats.movedIn ?? stats.total} of them moved into a lab he runs himself. Each piece keeps its versions, the piece it was forked from, and in some cases the poem written for it. New pieces also keep every decision he makes along the way.`,
    enter: 'Enter the Lab',
    all: 'Every piece',
    lineage: 'Family tree',
    specimenLabel: 'A piece running in the Lab',
    posterAlt: (n) => `${n}, about ten seconds after it starts in the Lab`,
    frameTitle: (n) => `${n} (running from lab.cheyuwu.com)`,
    playBtn: 'Play',
    stop: 'Stop',
    next: 'Another piece',
    openRun: 'Open in a new window',
    statusRunning: 'Running live from lab.cheyuwu.com',
    statusLoading: 'Loading',
    statusPoster: 'A frame ten seconds in. The piece starts running here in a moment',
    statusHeld: 'Your device asks for reduced motion or data saving, so press Play to run it',
    statusStopped: 'Stopped. Press Play to run it again',
    numbersLabel: 'The Lab in numbers',
    sTotal: 'pieces',
    sFeatured: 'selected by him',
    sPoems: 'poems',
    sFamilies: (pairs) => `families, plus ${pairs} pairs`,
    sDeepest: 'deepest family line',
    gens: ' gen.',
    sYears: 'years',
    barsLabel: 'Pieces per year',
    barTitle: (y) => `${y.year}: ${y.n} pieces, ${y.featured} selected`,
    barsCaption: 'Pieces per year, selected ones in gold',
    sourceLive: (d) => `Numbers read from lab.cheyuwu.com's public data (/api/index.json, /api/lineage.json) when this site was built on ${fmtDay(d, false)}.`,
    sourceSnapshot: (d) => `Numbers from the snapshot of ${fmtDay(d, false)}: this build could not reach lab.cheyuwu.com, the next build will try again.`,
    navLabel: 'Sections of this page',
    sections: [
      { id: 'lab-evolving', label: 'Now evolving' },
      { id: 'lab-grown', label: 'Grown into works' },
      { id: 'lab-wall', label: 'Daily coding' },
      { id: 'lab-lineage', label: 'One grows from another' },
      { id: 'lab-poem', label: 'A poem' },
      { id: 'lab-play', label: 'How to play' },
      { id: 'lab-why', label: 'Why' },
    ],
    wallTitle: 'Daily coding',
    wallNote: (f) => `Thirty of the ${f} selected pieces, taken from each year in proportion and laid out oldest first. The six large ones carry poems. Each opens in the Lab, where the piece runs in the page.`,
    allFeatured: (f) => `All ${f} selected pieces`,
    evoTitle: 'Now evolving',
    evoNote: "New pieces still growing in the Lab, the most recently changed first. The list and its order are the Lab's own Now evolving page; private pieces show only a name and a thumbnail.",
    evoEmpty: 'Nothing is evolving right now.',
    evoAll: (n) => `All ${n} in the Lab`,
    evoSourceLive: (d) => `List read from lab.cheyuwu.com's public data (/api/evolving.json) when this site was built on ${fmtDay(d, false)}.`,
    evoSourceSnapshot: (d) => `List from the snapshot of ${fmtDay(d, false)}: this build could not reach lab.cheyuwu.com, the next build will try again.`,
    grownTitle: 'Grown into works',
    grownNote: (n, k) => `${n} works on this site trace their family tree back to the Lab: ${k} daily sketches that later grew into works that were exhibited or released. They are ordered by the date of the sketch, oldest first. Each gene strip is cut from the work's own image, and the sketch name leads back to that day's code.`,
    category: (c) => ({ installation: 'Installation', generative: 'Generative art', nft: 'Generative art', opensource: 'Open source', research: 'Research' })[c] || c,
    fromPre: 'Grew from ',
    fromPost: '',
    fromPrivate: 'the sketch is private',
    grownWorks: 'All works',
    growthTitle: 'One grows from another',
    beeStory: 'On 12 January 2021 Che-Yu Wu wrote a sketch called Bee and forked a second version the same day. Two years later, on 20 May 2023, that code grew into Three Stages of Reconstruction, and over the next fourteen days sixteen more pieces grew out of it, down to Feathering in the eighth generation.',
    familyGeneric: (n, size, gens) => `The ${n} family: ${size} pieces across ${gens} generations.`,
    legend: 'Each column is a generation and each line a fork. Every piece opens in the Lab.',
    thisFamily: (n) => `The ${n} family`,
    allFamilies: (f, p) => `All ${f} families and ${p} pairs`,
    officialLineage: 'Family tree on this site',
    deepestNote: (name, size, gens, works, fam, fsize, fgens) => `The deepest line in the Lab is the ${name} family, ${size ? `${size} pieces across ` : ''}${gens} generations${works.length ? `; ${listEn(works)} on this site grew out of it` : ''}. The tree drawn here is the ${fam} family, ${fsize} pieces across ${fgens} generations.`,
    treeHint: (g) => `Swipe sideways to see all ${g} generations`,
    recentTitle: 'Most recent',
    recentNote: 'Newest first, teaching demos left out, the same list as Every piece in the Lab.',
    nodeLabel: (n, d, g) => `${n}, ${d}, generation ${g}`,
    gen: (g) => `Gen ${g}`,
    yearsLater: (y) => `${y} ${y === 1 ? 'year' : 'years'} later`,
    monthsLater: (m) => `${m} months later`,
    poemTitle: 'A poem',
    poemNote: (all, pure) => `${all} pieces in the Lab carry poems, ${pure} of them pure poems. This is the same pool as the Lab's front page, and it changes every time you open the page. Poems are shown in Chinese, as he wrote them.`,
    nextPoem: 'Another poem',
    seePiece: 'See the piece',
    playTitle: 'How to play',
    ownerOnly: 'Open only to Che-Yu for now',
    play: [
      { key: 'run', title: 'Run it in the page', body: 'Every piece runs as soon as you open it, some follow the mouse, and a new window gives you full screen.', links: [{ label: 'Try Curvy Vine', href: `${LAB}/s/200925-curvy-vine/` }, { label: 'Open in a new window', href: `${LAB}/run/200925-curvy-vine/` }] },
      { key: 'midi', title: 'MIDI', body: 'Pieces that use MIDI show a 🎹 in the corner. It opens on-screen controllers (MiniLab 3, LPD8, Launchpad, nanoKONTROL2) you can play with the mouse. Plug in a real controller and the piece follows your hands.', links: [{ label: 'Try Prebiotic Flake', href: `${LAB}/s/230616-prebiotic-flake/` }, { label: 'Controller source', href: 'https://openaudiovisual.com/#work-09' }] },
      { key: 'phone', owner: true, title: 'Phone controller', body: 'In the studio each piece has its own room, and a phone that scans its QR code becomes its control surface: tilt, keys and sliders all move the piece. This layer is open only to Che-Yu for now. The controller itself is open source in open-audiovisual.', links: [{ label: 'Controller source', href: 'https://openaudiovisual.com/#work-08' }] },
      { key: 'talk', owner: true, title: 'Talk to a piece', body: 'Once a piece has a name and a poem, it can speak. For now only Che-Yu can talk to it after signing in, through the 💬 on the piece page.', links: [{ label: 'The page for Flourishing', href: `${LAB}/s/221230-flourishing/` }] },
      { key: 'fork', title: 'Fork', body: 'Any piece can grow out of another, taking the code, the memory, or both. The family tree draws every fork, so from any piece you can climb back to its source.', links: [{ label: 'The Lab family tree', href: `${LAB}/lineage/` }] },
      { key: 'api', title: 'For code and AI', body: 'The data for every public piece sits under /api/, readable by programs and AI agents without opening a browser.', links: [{ label: '/api/index.json', href: `${LAB}/api/index.json` }] },
    ],
    whyTitle: 'Why build his own',
    why1: 'My principle for making work from now on: one is to experiment without any friction… the other is that whenever I enter a site, a medium, a space, I try each time, early on, to lower my future friction and turn everything into experience.',
    whyCite: 'Che-Yu Wu, 3 October 2026 (translated from Chinese)',
    whyText1: 'OpenProcessing kept his versions and forks, but had no place for the poems and no way back to the works that later went on show. The Lab adds both: each piece page has its poem and a field guide, and links to the work on this site that grew out of it.',
    why2: 'Once things got faster, what stopped growing was my drawing by hand.',
    whyText2: 'So the first seed of a new piece comes, as far as possible, from his hand: a sketch on the iPad, or a phrase played at the piano.',
    enterSmall: 'Enter the Lab',
    strata: (a, b) => `Strata: what each year left behind, ${a}–${b}`,
    api: 'Public data API',
  },
}
const t = computed(() => messages[zh.value ? 'zh' : 'en'])
// 怎麼玩：大家都能用的在前，只開給哲宇的兩項（手機控制器、跟作品講話）併成最後一列
const playOpen = computed(() => t.value.play.filter((p) => !p.owner))
const playOwner = computed(() => t.value.play.filter((p) => p.owner))
</script>

<style scoped>
/* 實驗室頁：跟音樂、關於同一套（--editorial-w、Noto Serif TC 大標、eyebrow、無框膠囊、--bg1 面）。
   圖片、縮圖、作品 iframe 一律直角（DESIGN.md §十八）。動態只有狀態燈、海報淡出、換詩淡入，減少動態時都關掉。 */
.lab-page {
  --lp-warm: #b7ad9b;
  max-width: var(--editorial-w, 1560px);
  margin: auto;
  padding: 64px 40px 0;
  color: var(--ink);
  font-family: var(--font-body);
  text-align: left;
  overflow-wrap: anywhere;
}
h1, h2, h3 { font-family: var(--font-display); font-weight: 400; }
a { color: var(--gold); text-decoration: none; transition: var(--t-hover); }
a:hover { color: var(--ink); }
a:focus-visible, button:focus-visible { outline: 2px solid var(--gold); outline-offset: 4px; }
.lp-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.lp-eyebrow { color: var(--lp-warm); font-size: 13px; letter-spacing: 0.08em; margin: 0; }

/* ── 頁首 ── */
.lp-hero { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 72px; align-items: center; margin-bottom: 72px; }
.lp-hero h1 { font-size: clamp(64px, 8vw, 120px); line-height: 1; margin: 24px 0 8px; }
.lp-hero h1 small { display: block; margin-top: 14px; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 13px; letter-spacing: 0.16em; color: var(--ink-3); }
.lp-motto { font-size: clamp(21px, 2.1vw, 28px); line-height: 1.7; margin: 40px 0 28px; max-width: 22em; }
.lp-lede { color: var(--ink-2); font-size: 17px; line-height: 1.9; max-width: 34em; margin: 0 0 28px; }
.lp-actions { display: flex; flex-wrap: wrap; gap: 12px; }
.lp-actions a, .lp-frame-actions button, .lp-frame-actions a, .lp-poem-actions button, .lp-poem-actions a {
  display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 10px 20px;
  border: 0; border-radius: var(--r-pill); color: var(--ink); background: var(--tint-2); font: inherit; font-size: 15px; cursor: pointer; transition: var(--t-hover);
}
.lp-actions a:hover, .lp-frame-actions button:hover, .lp-frame-actions a:hover, .lp-poem-actions button:hover, .lp-poem-actions a:hover { background: var(--tint-3); color: var(--ink); }
.lp-actions a.primary { background: #c5af7f; color: #080808; }
.lp-actions a.primary:hover { background: var(--gold); color: #000; }

.lp-specimen { margin: 0; }
.lp-frame { position: relative; width: 100%; overflow: hidden; background: #050505; }
.lp-poster { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; z-index: 1; transition: opacity var(--dur-scene) var(--ease-out); }
.lp-frame.is-live .lp-poster { opacity: 0; pointer-events: none; }
@media (max-width: 960px) { .lp-poster.is-lqip { filter: blur(14px); transform: scale(1.08); } }
.lp-live { position: absolute; left: 0; top: 0; border: 0; transform-origin: 0 0; display: block; background: #000; }
.lp-play-mini {
  position: absolute; z-index: 2; left: 12px; bottom: 12px;
  display: inline-flex; align-items: center; gap: 8px; min-height: 40px; padding: 8px 16px;
  border: 0; border-radius: var(--r-pill); background: rgba(0, 0, 0, 0.62); color: var(--ink); font: inherit; font-size: 14px; cursor: pointer;
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px); transition: var(--t-hover);
}
.lp-play-mini .brand-icon { color: var(--gold); width: 16px; height: 16px; }
/* 執行／停止記號：2023 品牌圖示 Run／Stop（2026-10-04 取代字元 ▶，文字保留） */
.lp-btn-icon { display: inline-flex; align-items: center; gap: 8px; }
.lp-btn-icon .brand-icon { width: 16px; height: 16px; }
/* 頁首印章：Lab 圖示（viewBox 貼齊圖形）；桌機 88px 高、手機 56px，左緣對齊下面的字 */
.lp-mark { display: block; width: auto; height: 88px; margin: 0 0 28px; color: var(--ink); }
.lp-play-mini:hover { background: rgba(0, 0, 0, 0.8); }
.lp-specimen-bar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 12px 20px; margin-top: 14px; }
/* 狀態字自己一列、留兩行高：字從「執行十秒時的畫面」換成「載入中」「正在執行」時，下面的東西不會跳（CLS） */
.lp-status { flex: 1 1 100%; display: flex; align-items: baseline; gap: 10px; margin: 0; min-height: 3em; color: var(--ink-3); font-size: 13px; line-height: 1.5; }
.lp-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--ink-4); transform: translateY(-1px); }
.lp-dot.on { background: var(--gold); box-shadow: 0 0 0 0 rgba(217, 194, 103, 0.55); animation: lp-pulse 2.4s var(--ease-out) infinite; }
@keyframes lp-pulse { 0% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0.5) } 70% { box-shadow: 0 0 0 9px rgba(217, 194, 103, 0) } 100% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0) } }
.lp-frame-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.lp-frame-actions button, .lp-frame-actions a { min-height: 40px; padding: 8px 16px; font-size: 14px; }
.lp-specimen-title { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 6px 16px; margin: 18px 0 0; }
.lp-specimen-title a { color: var(--ink); display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: 4px 12px; }
.lp-specimen-title a:hover b { color: var(--gold); }
.lp-specimen-title b { font-family: var(--font-display); font-weight: 400; font-size: 22px; transition: var(--t-hover); }
.lp-specimen-title span { color: var(--ink-3); font-size: 14px; }
code { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; letter-spacing: 0.04em; color: var(--ink-3); background: none; padding: 0; }
/* 頁首作品的基因條：作品頁的標本標籤（320×20、手機 240×16），左緣對齊作品名。先留好高度，條載到才淡入（CLS 0） */
.lp-hero-gene { display: block; --gene-h: 20px; width: 320px; max-width: 100%; height: 20px; margin-top: 16px; }

/* ── 數字：一列六個（2026-10-05 收成一列，每年的長條搬到 03 每日創作）。數字在上、說明在下（dt 在 DOM 前面，用 order 排） ── */
.lp-numbers { padding: 36px 0 30px; border-top: 1px solid var(--line); }
.lp-stats { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 28px 24px; margin: 0 0 22px; }
.lp-stats div { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.lp-stats dd { order: -1; margin: 0; font-family: var(--font-display); font-size: clamp(34px, 3.4vw, 52px); line-height: 1; font-variant-numeric: tabular-nums; }
.lp-stats dd small { font-size: 0.42em; margin-left: 4px; color: var(--ink-2); }
.lp-stats .lp-years-dd { font-size: clamp(24px, 2.2vw, 34px); padding-top: 0.35em; }
.lp-stats dt { color: var(--ink-2); font-size: 14px; font-weight: 400; line-height: 1.5; }
.lp-stats .lp-stat-of { order: 1; font-family: var(--font-body); font-size: 13px; line-height: 1.4; }
.lp-stats .lp-stat-of a { display: inline-flex; min-height: 24px; align-items: center; }
.lp-source { margin: 0; color: var(--ink-3); font-size: 13px; line-height: 1.6; }

/* ── 每一年（03 每日創作的上面一列）：件數長條、金色是精選、那一年的一條基因條，點了到實驗室那一年。
   桌機一年一欄（條各自分開，不連成一條），手機一年一列（橫的長條） ── */
.lp-bars { margin: 0 0 44px; }
.lp-bars ul { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(var(--years, 9), minmax(0, 1fr)); gap: 0 28px; align-items: end; }
.lp-bars li { min-width: 0; }
.lp-year { display: flex; flex-direction: column; justify-content: flex-end; height: 200px; color: var(--ink-2); }
.lp-year:hover { color: var(--ink); }
.lp-year:hover .lp-bar { background: rgba(255, 255, 255, 0.16); }
.lp-bar-n { font-size: 12px; color: var(--ink-3); font-variant-numeric: tabular-nums; margin-bottom: 6px; }
.lp-bar { position: relative; height: calc(var(--h) * 110px); min-height: 2px; width: 100%; max-width: 120px; background: var(--tint-3); display: block; transition: background var(--dur-quick) var(--ease-out); }
.lp-bar-f { position: absolute; left: 0; right: 0; bottom: 0; height: calc(var(--f) * 100%); background: var(--gold); opacity: 0.85; }
.lp-bar-y { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; margin-top: 10px; letter-spacing: 0.02em; }
.lp-year-gene { display: block; --gene-h: 10px; height: 10px; width: 100%; max-width: 120px; margin-top: 10px; }
.lp-bars figcaption { margin-top: 16px; color: var(--ink-3); font-size: 13px; }

/* ── 長成作品：卡片跟 /work/、首頁精選同一套（無框、圖直角、年份・類別、Display 字作品名、基因條在文字區、不貼圖） ── */
.lp-grown { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 52px 24px; }
.lp-grown-card { position: relative; min-width: 0; }
.lp-grown-img { display: block; aspect-ratio: 3 / 2; overflow: hidden; background: var(--bg1); margin-bottom: 16px; }
.lp-grown-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform var(--dur-scene) var(--ease-out); }
.lp-grown-card:hover .lp-grown-img img { transform: scale(1.025); }
.lp-grown-kicker { margin: 0; color: var(--lp-warm); font-size: 13px; letter-spacing: 0.04em; }
.lp-grown h3 { font-size: clamp(19px, 1.45vw, 23px); line-height: 1.35; margin: 6px 0 0; }
.lp-grown h3 a { color: var(--ink); }
.lp-grown-card:hover h3 a { color: var(--gold); }
/* 整張卡點得到作品頁，草圖那一行疊在上面，點得到草圖頁 */
.lp-stretch::after { content: ''; position: absolute; inset: 0; z-index: 1; }
.lp-grown-gene { display: block; --gene-h: 14px; height: 14px; width: 200px; max-width: 100%; margin-top: 14px; }
.lp-grown-from { position: relative; z-index: 2; display: flex; gap: 12px; align-items: center; margin: 16px 0 0; color: var(--ink-2); font-size: 14px; line-height: 1.55; }
.lp-seed { flex: none; display: block; width: 44px; height: 44px; background: var(--bg1); }
.lp-seed img { width: 100%; height: 100%; object-fit: cover; display: block; }
.lp-grown-from-text { min-width: 0; }
.lp-grown-from-text a { color: var(--ink); }
.lp-grown-from-text a:hover { color: var(--gold); }
.lp-grown-from-text b { font-weight: 400; color: var(--ink); }
.lp-grown-from-text small { display: block; margin-top: 2px; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 11.5px; color: var(--ink-3); letter-spacing: 0.02em; }
.lp-grown-more { grid-column: auto / -1; display: flex; flex-direction: column; justify-content: flex-end; align-items: flex-start; gap: 4px; min-height: 120px; }
.lp-grown-more a { display: inline-flex; align-items: center; min-height: 40px; }

.lp-nav { display: flex; flex-wrap: wrap; justify-content: flex-start; gap: 4px 36px; margin: 0; padding: 8px 0; font-weight: 400; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
.lp-nav a { display: flex; align-items: center; min-height: 44px; color: #c5af7f; }
.lp-nav a:hover { color: var(--ink); }

/* ── 章節 ── */
.lp-section { padding: 88px 0; border-bottom: 1px solid var(--line); scroll-margin-top: 90px; }
/* 第一屏以下的章節捲到附近才排版：詩、族譜、標題的 Noto Serif TC 字型切片與縮圖也等到那時才下載（手機 Lighthouse：字型 20 支 1.4 MB → 10 支）。
   預估高度照 2026-10-04 實量（1440／1024／390 中英平均，扣掉上下 padding：intrinsic size 量的是內容盒）：估太高，捲到底時頁面會縮、捲軸會跳。
   2026-10-05 改版後 1440 與 390 重量（中英平均）：長成作品、每日創作（多了年份列）、族譜、詩、怎麼玩都換了數字。
   正在演化 2026-10-05 實量（當天 1 件，件數變多會再長，auto 會記住實際高度） */
.lp-section { content-visibility: auto; contain-intrinsic-size: auto var(--ci, 1000px); }
#lab-evolving { --ci: 620px; }
#lab-grown { --ci: 1850px; }
#lab-wall { --ci: 1480px; }
#lab-lineage { --ci: 1040px; }
#lab-poem { --ci: 330px; }
#lab-play { --ci: 940px; }
#lab-why { --ci: 620px; }
.lp-head { margin-bottom: 36px; max-width: 46em; }
.lp-head h2 { font-size: clamp(32px, 4vw, 52px); line-height: 1.2; margin: 12px 0 14px; }
.lp-note { color: var(--ink-2); font-size: 15px; line-height: 1.8; margin: 0; }
.lp-body { font-size: 17px; line-height: 1.95; color: var(--ink); margin: 0 0 20px; max-width: 36em; }
.lp-legend { color: var(--ink-3); font-size: 13px; line-height: 1.7; margin: 0 0 18px; }
.lp-links { display: flex; flex-wrap: wrap; gap: 8px 24px; margin: 0; }
.lp-links a { display: inline-flex; align-items: center; min-height: 40px; }
.lp-more { display: flex; flex-wrap: wrap; gap: 8px 28px; margin: 28px 0 0; }
.lp-more a { display: inline-flex; align-items: center; min-height: 44px; }
#lab-evolving .lp-more { margin-top: 32px; }
#lab-evolving .lp-source { margin-top: 8px; }

/* 牆：8 欄（桌機）、6 欄、4 欄（手機）；放大格 2×2；dense 讓 48／48／40 格剛好填滿 */
.lp-wall { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(8, minmax(0, 1fr)); grid-auto-flow: dense; gap: 6px; }
.lp-wall li { position: relative; aspect-ratio: 1 / 1; background: var(--bg1); }
.lp-wall li.big { grid-column: span 2; grid-row: span 2; }
.lp-wall a { position: absolute; inset: 0; display: block; overflow: hidden; color: var(--ink); }
.lp-wall img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform var(--dur-scene) var(--ease-out), opacity var(--dur-base) var(--ease-out); }
.lp-wall a:hover img, .lp-wall a:focus-visible img { transform: scale(1.03); }
.lp-wall a:focus-visible { outline-offset: -2px; }
.lp-year-chip { position: absolute; left: 6px; top: 6px; padding: 2px 7px; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 11px; letter-spacing: 0.04em; color: var(--ink); background: rgba(0, 0, 0, 0.62); }
.lp-tile-meta {
  position: absolute; left: 0; right: 0; bottom: 0; padding: 26px 10px 8px; display: flex; flex-direction: column; gap: 2px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.82)); opacity: 0; transform: translateY(6px);
  transition: opacity var(--dur-quick) var(--ease-out), transform var(--dur-base) var(--ease-out);
}
.lp-wall a:hover .lp-tile-meta, .lp-wall a:focus-visible .lp-tile-meta { opacity: 1; transform: none; }
.lp-tile-meta b { font-weight: 500; font-size: 13px; line-height: 1.35; }
.lp-wall li.big .lp-tile-meta b { font-family: var(--font-display); font-size: 18px; font-weight: 400; }
.lp-tile-meta time { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 10.5px; color: var(--ink-2); }

/* 族譜：桌機代＝欄（左到右），手機代＝列（上到下）。位置都用格子中心的百分比，SVG 用同一套格子座標 */
.lp-growth-grid { display: grid; grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.6fr); gap: 56px; align-items: start; }
.lp-recent { margin-top: 44px; }
.lp-recent h3 { font-size: 20px; margin: 0 0 12px; }
.lp-recent ol { list-style: none; margin: 0 0 10px; padding: 0; }
/* 最近長出來的：日期、名字、右邊一枚短基因條（96×10，從那件的縮圖切） */
.lp-recent li { display: flex; gap: 16px; align-items: baseline; padding: 9px 0; border-bottom: 1px solid var(--line); }
.lp-recent time { flex: none; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; color: var(--ink-3); }
.lp-recent a { color: var(--ink); min-width: 0; }
.lp-recent a:hover { color: var(--gold); }
.lp-recent-gene { flex: none; display: block; --gene-h: 10px; width: 96px; height: 10px; margin-left: auto; align-self: center; }
.lp-deepest { color: var(--ink-2); font-size: 15px; line-height: 1.9; margin-bottom: 4px; }
.lp-deepest-link { margin-bottom: 18px; }
.lp-tree-fig { margin: 0; }
.lp-tree { --node: 52px; position: relative; width: 100%; height: calc(var(--R) * 66px); }
.lp-edges { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.lp-edge { fill: none; stroke: var(--ink-4); stroke-width: 1.5; }
.lp-edge.sim { stroke-dasharray: 4 4; }
.lp-edge.gap { stroke: var(--gold); stroke-dasharray: 2 5; opacity: 0.9; }
.lp-node {
  position: absolute; width: var(--node); height: var(--node); display: block;
  left: calc((var(--g) + 0.5) / var(--G) * 100% - var(--node) / 2);
  top: calc((var(--r) + 0.5) / var(--R) * 100% - var(--node) / 2);
}
.lp-node img { width: 100%; height: 100%; object-fit: cover; display: block; background: var(--bg1); box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08); transition: box-shadow var(--dur-quick) var(--ease-out), transform var(--dur-base) var(--ease-out); }
.lp-node:hover img, .lp-node:focus-visible img { box-shadow: 0 0 0 2px var(--gold); transform: scale(1.08); }
.lp-node.root img { box-shadow: 0 0 0 2px var(--gold); }
.lp-node-date { position: absolute; left: 50%; top: calc(100% + 3px); transform: translateX(-50%); font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 10px; color: var(--ink-3); white-space: nowrap; }
.lp-gap-label {
  position: absolute; z-index: 1; transform: translate(-50%, -150%); white-space: nowrap;
  left: calc((var(--g) + 0.5) / var(--G) * 100%); top: calc((var(--r) + 0.5) / var(--R) * 100%);
  font-size: 12px; color: var(--gold); background: var(--bg0); padding: 1px 6px;
}
.lp-gens { position: relative; height: 22px; margin-top: 14px; }
.lp-gen { position: absolute; left: calc((var(--g) + 0.5) / var(--G) * 100%); transform: translateX(-50%); font-size: 12px; color: var(--ink-3); white-space: nowrap; }
.lp-tree-hint { display: none; margin-top: 10px; color: var(--ink-3); font-size: 12px; }

/* 詩 */
#lab-poem { padding: 72px 0; }
#lab-poem .lp-head { margin-bottom: 28px; }
.lp-poem-fig { margin: 0; display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 300px); gap: 40px 72px; align-items: end; transition: opacity 180ms var(--ease-out); }
.lp-poem-fig.swapping { opacity: 0; }
.lp-poem-lines { margin: 0; padding: 0; border: 0; font-family: var(--font-display); font-size: clamp(22px, 2.5vw, 34px); line-height: 1.8; color: var(--ink); }
.lp-poem-lines p { margin: 0; }
.lp-line { display: block; }
.lp-line-gap { display: block; height: 0.9em; }
.lp-poem-cap { display: grid; grid-template-columns: 96px minmax(0, 1fr); gap: 16px; align-items: start; }
.lp-poem-thumb img { width: 96px; height: 96px; object-fit: cover; display: block; background: var(--bg1); }
.lp-poem-meta { display: flex; flex-direction: column; gap: 4px; }
.lp-poem-meta > a { color: var(--ink); }
.lp-poem-meta > a:hover { color: var(--gold); }
.lp-poem-meta b { font-family: var(--font-display); font-weight: 400; font-size: 20px; }
.lp-poem-meta small { color: var(--ink-3); font-size: 13px; }
.lp-poem-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.lp-poem-actions button, .lp-poem-actions a { min-height: 40px; padding: 8px 16px; font-size: 14px; }

/* 怎麼玩（2026-10-05）：兩欄清單、直角、每項上面一條細線（原本六張圓角面板，手機上疊成 1,911px 高）。
   只開給哲宇的兩項（手機控制器、跟作品講話）併成最後一整列，字小一級、灰一點 */
.lp-play-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 64px; }
.lp-play-list > li { padding: 26px 0 28px; border-top: 1px solid var(--line); display: flex; flex-direction: column; }
.lp-play-no { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; color: var(--ink-3); letter-spacing: 0.08em; }
.lp-play-list h3 { font-size: 23px; margin: 8px 0 10px; }
.lp-play-list p { color: var(--ink-2); font-size: 15px; line-height: 1.85; margin: 0 0 12px; max-width: 36em; }
.lp-play-list .lp-links { margin-top: auto; }
.lp-play-list > li.lp-play-owner { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 64px; }
.lp-play-owner-label { grid-column: 1 / -1; margin: 0 0 6px !important; color: var(--lp-warm) !important; font-size: 13px !important; letter-spacing: 0.04em; }
.lp-play-owner-item { display: flex; flex-direction: column; padding-top: 10px; }
.lp-play-owner-item h3 { font-size: 19px; color: var(--ink-2); }
.lp-play-owner-item p { font-size: 14px; }

/* 為什麼：標題在左，原話與正文在右（跟關於頁〈從模擬器，到共生〉同一種左右分欄） */
.lp-why { display: grid; grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.6fr); gap: 0 56px; align-items: start; }
.lp-why-body { max-width: 46em; }
.lp-why-body .quote { font-size: clamp(19px, 1.7vw, 23px); margin: 8px 0 28px; }
.lp-why-body .quote + .lp-body { margin-bottom: 44px; }

.lp-outro { padding: 88px 0 96px; display: flex; flex-direction: column; gap: 28px; }
.lp-outro-main { display: inline-flex; flex-direction: column; gap: 10px; align-self: flex-start; font-family: var(--font-display); font-size: clamp(30px, 6vw, 88px); line-height: 1.05; color: var(--ink); white-space: nowrap; overflow-wrap: normal; }
.lp-outro-main small { font-family: var(--font-body); font-size: 14px; letter-spacing: 0.08em; color: var(--lp-warm); }
.lp-outro-main:hover { color: var(--gold); }

@media (max-width: 1200px) {
  .lp-wall { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  .lp-grown { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .lp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .lp-bars ul { gap: 0 16px; }
}
@media (max-width: 960px) {
  .lab-page { padding: 40px 16px 0; }
  /* 手機：作品排在大標正下方，第一屏就看得到它在跑（哲宇 10/04「首 section 直接跑起來」）；文字容器拆開讓子元素直接排進格線 */
  .lp-hero { grid-template-columns: minmax(0, 1fr); gap: 0; margin-bottom: 48px; }
  .lp-hero-text { display: contents; }
  .lp-hero .lp-mark { order: 0; height: 56px; margin-bottom: 20px; }
  .lp-hero .lp-eyebrow { order: 1; }
  .lp-hero h1 { order: 2; margin-bottom: 0; }
  .lp-specimen { order: 3; margin: 24px 0 8px; }
  .lp-motto { order: 4; }
  .lp-lede { order: 5; }
  .lp-actions { order: 6; }
  .lp-growth-grid, .lp-why { grid-template-columns: minmax(0, 1fr); gap: 40px; }
  .lp-grown { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  #lab-evolving { --ci: 510px; }
  #lab-grown { --ci: 2600px; }
  #lab-wall { --ci: 1600px; }
  #lab-lineage { --ci: 1500px; }
  #lab-poem { --ci: 350px; }
  #lab-play { --ci: 1000px; }
  #lab-why { --ci: 600px; }
  .lp-poem-fig { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 700px) {
  .lp-motto { margin-top: 28px; }
  .lp-lede { font-size: 16px; }
  .lp-hero-gene { --gene-h: 16px; width: 240px; height: 16px; margin-top: 14px; }
  .lp-numbers { padding: 28px 0 24px; }
  .lp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px 14px; }
  .lp-stats dd { font-size: 30px; }
  .lp-stats .lp-years-dd { font-size: 19px; padding-top: 0.5em; }
  .lp-stats dt { font-size: 12.5px; }
  .lp-stats .lp-stat-of { font-size: 12px; }
  .lp-section { padding: 64px 0; }
  #lab-poem { padding: 56px 0; }
  #lab-evolving { --ci: 580px; }
  #lab-grown { --ci: 2260px; }
  #lab-wall { --ci: 1580px; }
  #lab-lineage { --ci: 1830px; }
  #lab-poem { --ci: 530px; }
  #lab-play { --ci: 1700px; }
  #lab-why { --ci: 1040px; }
  /* 每一年：一年一列，橫的長條（年份｜長條｜件數｜基因條），整列點得到 */
  .lp-bars { margin-bottom: 36px; }
  .lp-bars ul { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .lp-year { display: grid; grid-template-columns: 40px minmax(0, 1fr) 32px 72px; grid-template-areas: 'y bar n gene'; align-items: center; gap: 12px; height: auto; min-height: 44px; border-bottom: 1px solid var(--line); }
  .lp-bar-n { grid-area: n; margin: 0; text-align: right; }
  .lp-bar { grid-area: bar; height: 8px; min-height: 0; width: calc(var(--h) * 100%); min-width: 2px; }
  .lp-bar-f { top: 0; bottom: 0; right: auto; height: auto; width: calc(var(--f) * 100%); }
  .lp-bar-y { grid-area: y; margin: 0; }
  .lp-year-gene { grid-area: gene; margin: 0; width: 72px; }
  /* 長成作品：一件一列，圖在左、字在右，草圖那一行整寬 */
  .lp-grown { grid-template-columns: minmax(0, 1fr); gap: 26px; }
  .lp-grown-card { display: grid; grid-template-columns: 112px minmax(0, 1fr); column-gap: 14px; align-items: start; }
  .lp-grown-img { grid-column: 1; grid-row: 1 / span 3; margin: 0; }
  .lp-grown-card > :not(.lp-grown-img) { grid-column: 2; }
  .lp-grown-card > .lp-grown-from { grid-column: 1 / -1; }
  .lp-grown-kicker { font-size: 12px; }
  .lp-grown h3 { font-size: 17px; margin-top: 2px; }
  .lp-grown-gene { width: 100%; max-width: 180px; --gene-h: 12px; height: 12px; margin-top: 8px; }
  .lp-grown-from { margin-top: 10px; gap: 10px; font-size: 13px; line-height: 1.5; }
  .lp-seed { width: 32px; height: 32px; }
  .lp-grown-from-text small { font-size: 11px; }
  .lp-grown-more { grid-column: 1; min-height: 0; flex-direction: row; flex-wrap: wrap; gap: 4px 24px; }
  .lp-wall { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 4px; }
  .lp-wall li.m-hide { display: none; }
  .lp-tile-meta { display: none; }
  .lp-play-list { grid-template-columns: minmax(0, 1fr); }
  .lp-play-list > li { padding: 22px 0 22px; }
  .lp-play-list > li.lp-play-owner { grid-template-columns: minmax(0, 1fr); }
  .lp-play-owner-item + .lp-play-owner-item { margin-top: 18px; }
  .lp-recent-gene { width: 72px; }
  /* 族譜：手機也是代＝欄，整張圖左右滑（節點 36px、每代 56px，八代大約一屏，2026-10-05 以前轉成直的、節點 30px 疊在一起） */
  .lp-tree-scroll { overflow-x: auto; overscroll-behavior-x: contain; margin: 0 -16px; padding: 0 16px 6px; scrollbar-width: thin; }
  .lp-tree, .lp-gens { width: calc(var(--G) * 56px); }
  .lp-tree { --node: 36px; height: calc(var(--R) * 52px); }
  .lp-gens { margin-top: 10px; }
  .lp-gen { font-size: 11px; }
  .lp-tree-hint { display: block; }
  .lp-outro { padding: 72px 0 96px; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-dot.on { animation: none; }
  .lp-poster, .lp-poem-fig, .lp-wall img, .lp-node img, .lp-tile-meta, .lp-grown-img img, .lp-bar { transition: none; }
  .lp-wall a:hover img, .lp-node:hover img, .lp-grown-card:hover .lp-grown-img img { transform: none; }
}
</style>
